// Implements: F-IDN-012, F-IDN-014, F-IDN-015, F-IDN-017
/**
 * The `account` module's client (mockup v3 P1): the own profile and photo, the password, the
 * two-step verification, the access log, the other sessions and the personal data.
 *
 * Every call that changes something is sent with `retry: false` (checklist 13): a password change,
 * a TOTP verification or an erasure that reached the server once and lost its answer must not run a
 * second time behind the person's back. Only the reads keep the client's retry of a 5xx.
 */

import { ApiError } from "../../../shared/api/ApiError";
import { API_BASE } from "../../../shared/api/apiBase";
import { httpDownload, httpRequest } from "../../../shared/api/httpClient";
import type { Download } from "../../../shared/api/httpClient";
import type {
  AccessLogPage,
  MeSummary,
  RevokeOthersResponse,
  TotpEnrolment,
} from "../../../types/api";

export const ME_PATH = `${API_BASE}/auth/me`;
export const AVATAR_PATH = `${API_BASE}/auth/me/avatar`;
export const PASSWORD_CHANGE_PATH = `${API_BASE}/auth/password-change`;
export const TOTP_ENROL_PATH = `${API_BASE}/auth/totp/enroll`;
export const TOTP_VERIFY_PATH = `${API_BASE}/auth/totp/verify`;
export const TOTP_PATH = `${API_BASE}/auth/totp`;
export const ACCESS_LOG_PATH = `${API_BASE}/auth/access-log`;
export const REVOKE_OTHERS_PATH = `${API_BASE}/auth/sessions/revoke-others`;

/** `/users/{id}/personal-data`, escaped: the id arrives from a response body. */
export function personalDataPath(userId: string): string {
  return `${API_BASE}/users/${encodeURIComponent(userId)}/personal-data`;
}

/** The largest photo the server keeps (Contract: 2 MiB); a bigger one is refused before sending. */
export const AVATAR_MAX_BYTES = 2 * 1024 * 1024;

/** The code `uploadAvatar` throws for a file refused on this side, with no request made. */
export const AVATAR_TOO_LARGE = "AVATAR_TOO_LARGE";

/** The only types the server keeps (Contract: PNG, JPEG or WebP; SVG never). */
export const AVATAR_TYPES: readonly string[] = ["image/png", "image/jpeg", "image/webp"];

/** The code `uploadAvatar` throws for a file of another type, with no request made. */
export const AVATAR_WRONG_TYPE = "AVATAR_WRONG_TYPE";

/** A body that must be there: a `200` with nothing in it is a broken contract, not a value. */
function required<T>(value: T | null | undefined, what: string): T {
  if (value === undefined || value === null) {
    throw new ApiError(200, "UNKNOWN", `${what} answered with no body`, new Headers());
  }
  return value;
}

export async function fetchMe(signal?: AbortSignal): Promise<MeSummary> {
  return required(await httpRequest<MeSummary | null>(ME_PATH, { signal }), "GET /auth/me");
}

export async function updateProfile(fullName: string): Promise<MeSummary> {
  const me = await httpRequest<MeSummary | null>(ME_PATH, {
    method: "PATCH",
    body: { full_name: fullName },
    retry: false,
  });
  return required(me, "PATCH /auth/me");
}

/** The file's bytes as base64, without the `data:` prefix a `FileReader` would add. */
async function base64Of(file: Blob): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  const chunk = 0x8000;
  for (let start = 0; start < bytes.length; start += chunk) {
    binary += String.fromCharCode(...bytes.subarray(start, start + chunk));
  }
  return btoa(binary);
}

/**
 * Upload a profile photo. A file over 2 MiB, or one the browser does not call PNG, JPEG or WebP, is
 * refused here, with no request: the server would refuse it too (it reads the type from the bytes,
 * which stays the real check), after the person waited for the upload.
 */
export async function uploadAvatar(file: Blob): Promise<MeSummary> {
  if (!AVATAR_TYPES.includes(file.type)) {
    throw new ApiError(422, AVATAR_WRONG_TYPE, "The photo is not a PNG, JPEG or WebP file", new Headers(), {
      details: { field: "image", reason: "type" },
    });
  }
  if (file.size > AVATAR_MAX_BYTES) {
    throw new ApiError(422, AVATAR_TOO_LARGE, "The photo is larger than 2 MiB", new Headers(), {
      details: { field: "image", reason: "too_large" },
    });
  }
  const me = await httpRequest<MeSummary | null>(AVATAR_PATH, {
    method: "PUT",
    body: { image_base64: await base64Of(file) },
    retry: false,
  });
  return required(me, "PUT /auth/me/avatar");
}

export async function removeAvatar(): Promise<MeSummary> {
  return required(
    await httpRequest<MeSummary | null>(AVATAR_PATH, { method: "DELETE", retry: false }),
    "DELETE /auth/me/avatar",
  );
}

export async function changePassword(current: string, next: string): Promise<void> {
  await httpRequest<unknown>(PASSWORD_CHANGE_PATH, {
    method: "POST",
    body: { current_password: current, new_password: next },
    retry: false,
  });
}

export async function enrolTotp(currentPassword: string): Promise<TotpEnrolment> {
  const enrolment = await httpRequest<TotpEnrolment | null>(TOTP_ENROL_PATH, {
    method: "POST",
    body: { current_password: currentPassword },
    retry: false,
  });
  return required(enrolment, "POST /auth/totp/enroll");
}

export async function verifyTotp(code: string): Promise<void> {
  await httpRequest<unknown>(TOTP_VERIFY_PATH, { method: "POST", body: { code }, retry: false });
}

export async function disableTotp(code: string): Promise<void> {
  await httpRequest<unknown>(TOTP_PATH, { method: "DELETE", body: { code }, retry: false });
}

/** The default page of the access log: the server's own bound is 1..200. */
export const ACCESS_LOG_LIMIT = 50;

/** What one page of the access log asks: where to continue and whether only failures count. */
export interface AccessLogQuery {
  readonly cursor?: string | undefined;
  readonly failedOnly?: boolean | undefined;
  readonly limit?: number | undefined;
}

/**
 * One page of the last 30 days (audit 2026-10-19): the server measures the window, filters
 * "Solo fallidos y bloqueados" (`failed_only=true`) and names the next page in `next_cursor`.
 */
export async function fetchAccessLogPage(query: AccessLogQuery = {}, signal?: AbortSignal): Promise<AccessLogPage> {
  const params = new URLSearchParams({ limit: String(query.limit ?? ACCESS_LOG_LIMIT) });
  if (query.cursor !== undefined) params.set("cursor", query.cursor);
  if (query.failedOnly === true) params.set("failed_only", "true");
  const body = required(
    await httpRequest<Partial<AccessLogPage> | null>(`${ACCESS_LOG_PATH}?${params.toString()}`, { signal }),
    "GET /auth/access-log",
  );
  return { records: required(body.records, "GET /auth/access-log"), next_cursor: body.next_cursor ?? null };
}

/** The key of one filter's pages of the access log, under the log's own prefix. */
export function accessLogPagesKey(failedOnly: boolean): readonly string[] {
  return [...ACCOUNT_ACCESS_LOG_KEY, "window", failedOnly ? "bad" : "all"];
}

export async function revokeOtherSessions(): Promise<RevokeOthersResponse> {
  return required(
    await httpRequest<RevokeOthersResponse | null>(REVOKE_OTHERS_PATH, { method: "POST", retry: false }),
    "POST /auth/sessions/revoke-others",
  );
}

export async function downloadPersonalData(userId: string): Promise<Download> {
  return httpDownload(personalDataPath(userId));
}

export async function erasePersonalData(userId: string): Promise<void> {
  await httpRequest<unknown>(personalDataPath(userId), { method: "DELETE", retry: false });
}

/** `GET /auth/me`: the own profile, its photo and its 2FA status. */
export const ACCOUNT_ME_KEY = ["account", "me"] as const;
/** `GET /auth/access-log`: the own sign-in attempts. */
export const ACCOUNT_ACCESS_LOG_KEY = ["account", "access-log"] as const;
